import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { OutreachForm } from '../models/outreach-form-data';
import { Connection } from '../models/connection.model';
import { CdfiFormData } from '../models/cdfi-form.model';
import { CdfiEmail } from '../models/cdfi-email.model';
import {
  MatchedConnectionsResponse,
  OutreachFormResponse,
} from '../models/outreach-form-response';

@Injectable({
  providedIn: 'root',
})
export class OutreachService {
  private apiBaseUrl = environment.apiBaseUrl;
  private http: HttpClient = inject(HttpClient);

  getFormType(
    uniqueId: string,
    businessRecordUniqueId: string | null = null,
  ): Observable<OutreachForm> {
    let url = `${this.apiBaseUrl}/api/cdfi-outreach/form/${uniqueId}`;
    if (businessRecordUniqueId) {
      url += `?unique_id=${businessRecordUniqueId}`;
    }
    return this.http.get<OutreachForm>(url);
  }

  getConnections(
    formId: string,
  ): Observable<{ connections: Connection[]; form_data: CdfiFormData }> {
    return this.http
      .get<{
        connections: Connection[];
        form_data: CdfiFormData;
      }>(`${this.apiBaseUrl}/api/cdfi-outreach/form/${formId}`)
      .pipe(
        map((response) => ({
          connections: this._reorderResponse(response.connections),
          form_data: response.form_data,
        })),
      );
  }

  submitForm(
    formId: string,
    formData: unknown,
  ): Observable<OutreachFormResponse> {
    return this.http.post<OutreachFormResponse>(
      `${this.apiBaseUrl}/api/cdfi-outreach/form/${formId}`,
      formData,
    );
  }

  getMatchedConnections(
    formId: string,
    jobId: number,
  ): Observable<MatchedConnectionsResponse> {
    return this.http.get<MatchedConnectionsResponse>(
      `${this.apiBaseUrl}/api/cdfi-outreach/form/${formId}/matching-status/${jobId}`,
      {
        headers: {
          'X-Skip-Loader': 'true',
        },
      },
    );
  }

  updateFileUploadStatus(formId: string, fileId: number): Observable<unknown> {
    return this.http.put<unknown>(
      `${this.apiBaseUrl}/api/cdfi-outreach/form/${formId}/attachments/${fileId}`,
      {},
      {
        headers: {
          'X-Skip-Loader': 'true',
        },
      },
    );
  }

  sendEmail(
    unique_id: string,
    outreachFormType: string,
  ): Observable<CdfiEmail> {
    return this.http.post<CdfiEmail>(
      `${this.apiBaseUrl}/api/record/${unique_id}/send-outreach/${outreachFormType}`,
      {},
    );
  }

  updateOutreachStatus(uniqueId: string, status: boolean): Observable<unknown> {
    return this.http.put<unknown>(
      `${this.apiBaseUrl}/api/cdfi-outreach/${uniqueId}/${status}`,
      {},
    );
  }

  private _reorderResponse<res>(response: res[]): res[] {
    return response.sort(() => Math.random() - 0.5);
  }

  submitFeedback(
    payload: { rating: number; comment: string },
    formId: string,
  ): Observable<unknown> {
    return this.http.post<unknown>(
      `${this.apiBaseUrl}/api/cdfi-outreach/form/${formId}/feedback`,
      payload,
      {
        headers: {
          'X-Skip-Loader': 'true',
        },
      },
    );
  }
}
